import { useCallback, useState } from "react"
import { Alert, Button, Paper, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import Loading from "components/loading"
import UpsellTabs from "components/material/upsell-tabs"
import RotatePatDialog from "components/personal-access-tokens/rotate-pat-dialog"
import RevokePatDialog from "components/personal-access-tokens/revoke-pat-dialog"
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard"
import { useNotifications } from "hooks/notifications"
import type { PatType } from "modules/user-profile/types"
import useAccountPats from "./hooks/use-account-pats"
import MemberRows, { TOKEN_LABEL } from "./components/member-rows"
import type { PatTokenTarget } from "./components/member-rows"
import TokensOffBanner from "./components/tokens-off-banner"

const CONTENT_SX = { p: 3 } satisfies SxProps<Theme>
const TITLE_SX = { mb: 2 } satisfies SxProps<Theme>

function targetSubject(target: PatTokenTarget | null): string {
  if (!target) return ""
  return `${target.memberName}'s ${TOKEN_LABEL[target.token.tokenType]}`
}

interface AccountPatsProps {
  /** The shared permissions tab set. Absent in tests, where `UpsellTabs` then renders nothing. */
  tabs?: { id: string, label: string }[]
}

export default function AccountPats({ tabs }: AccountPatsProps) {
  const {
    members,
    patEnabled,
    isPending,
    isError,
    isFetching,
    pending,
    refetch,
    createToken,
    revealToken,
    rotateToken,
    revokeToken,
  } = useAccountPats()
  const notifications = useNotifications()
  // `useCopyToClipboard` rather than `helpers/clipboard`: it awaits the write, reports whether it
  // landed, and falls back to `execCommand` where the async Clipboard API is absent — which
  // includes the older Safari versions still in our browserslist.
  const { copy } = useCopyToClipboard()
  const [rotateTarget, setRotateTarget] = useState<PatTokenTarget | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<PatTokenTarget | null>(null)

  const handleRetry = useCallback(() => refetch(), [refetch])
  const handleRotateClose = useCallback(() => setRotateTarget(null), [])
  const handleRevokeClose = useCallback(() => setRevokeTarget(null), [])

  // Reports whether the clipboard actually took the value, so the toast tells the truth. A
  // silent failure would leave the admin pasting whatever the clipboard held before.
  const copyAndReport = useCallback(async (secret: string) => {
    if (await copy(secret)) {
      notifications.success("Token copied to the clipboard.")
      return
    }
    notifications.error("Could not write to the clipboard. Check the browser's clipboard permission and copy again.")
  }, [copy, notifications])

  // Copy is the only way a token leaves this page, and the secret reaches a local const and
  // the clipboard, nothing else: no component state, no row data, so it is never drawn. Each
  // Copy is its own reveal, and so its own audit row — no client-side cache hides a disclosure.
  const handleCopy = useCallback(async (target: PatTokenTarget) => {
    try {
      const { secret } = await revealToken(target.token.id)
      await copyAndReport(secret)
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [copyAndReport, revealToken])

  // The create and rotate responses carry the new secret. It is dropped rather than shown or
  // held: the row's Copy button reaches the same value, so nothing is lost by letting it go.
  const handleCreate = useCallback(async (userId: string, type: PatType) => {
    try {
      await createToken({ userId, type })
      notifications.success("Token created. Use Copy to put it on the clipboard.")
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [createToken, notifications])

  const handleRotateConfirm = useCallback(async () => {
    const target = rotateTarget
    setRotateTarget(null)
    if (!target) return
    try {
      await rotateToken(target.token.id)
      notifications.success("Token rotated. Use Copy to put the new token on the clipboard.")
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [rotateTarget, rotateToken, notifications])

  const handleRevokeConfirm = useCallback(async () => {
    const target = revokeTarget
    setRevokeTarget(null)
    if (!target) return
    try {
      await revokeToken(target.token.id)
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [revokeTarget, revokeToken])

  // The tab bar sits outside the content, as it does on Members, Teams and Tokens, and renders
  // in the loading state too so the four tabs of this set do not flicker in and out.
  if (isPending) {
    return <>
      <UpsellTabs id="personal-tokens" tabs={tabs} />
      <Loading />
    </>
  }

  return <>
    <UpsellTabs id="personal-tokens" tabs={tabs} />
    <Paper className="modules_account-pats content" square sx={CONTENT_SX}>
      <Typography variant="h6" sx={TITLE_SX}>Personal Tokens</Typography>

      {/* `patEnabled` defaults to true until a response exists, so this only shows once the
          server has actually said tokens are off for the account. */}
      {!patEnabled && <TokensOffBanner />}

      {isError
        ? <Alert
            severity="error"
            action={<Button color="inherit" size="small" disabled={isFetching} onClick={handleRetry}>Retry</Button>}
        >
          We could not load the members and their tokens.
        </Alert>
        : <MemberRows
            members={members}
            patEnabled={patEnabled}
            pending={pending}
            onCreate={handleCreate}
            onCopy={handleCopy}
            onRotate={setRotateTarget}
            onRevoke={setRevokeTarget}
        />}

      <RotatePatDialog
        open={!!rotateTarget}
        subject={targetSubject(rotateTarget)}
        onClose={handleRotateClose}
        onConfirm={handleRotateConfirm}
      />

      <RevokePatDialog
        open={!!revokeTarget}
        subject={targetSubject(revokeTarget)}
        onClose={handleRevokeClose}
        onConfirm={handleRevokeConfirm}
      />
    </Paper>
  </>
}
